/**
 * 独享容器文件/进程/日志通道（JSON 文本帧 + 二进制帧）。
 * 请求 {id, op, ...}；响应 {id, ok, data, error}。下载/上传/推送日志见各方法。
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { t } from '../../i18n';
import { isOk, errMsg } from '../../services/containerService';

export const buildWsUrl = (base, params) => {
    let u = String(base || '');
    if (u.startsWith('/')) u = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}${u}`;
    else if (/^https?:/i.test(u)) u = u.replace(/^http/i, 'ws');
    const qs = Object.entries(params)
        .filter(([, v]) => v !== undefined && v !== null)
        .map(([k, v]) => `${k}=${encodeURIComponent(v)}`)
        .join('&');
    return u + (u.includes('?') ? '&' : '?') + qs;
};

export const b64ToBytes = (b64) => {
    const bin = atob(b64 || '');
    const u = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i += 1) u[i] = bin.charCodeAt(i);
    return u;
};
export const bytesToB64 = (u) => {
    let s = '';
    for (let i = 0; i < u.length; i += 0x8000) s += String.fromCharCode.apply(null, u.subarray(i, i + 0x8000));
    return btoa(s);
};

export class FilesSocket {
    constructor() {
        this.ws = null;
        this.seq = 0;
        this.pending = new Map();
        this.listeners = new Map();
        this.dl = null;
        this.onClose = null;
    }

    open(url) {
        return new Promise((resolve, reject) => {
            let opened = false;
            const ws = new WebSocket(url);
            ws.binaryType = 'arraybuffer';
            this.ws = ws;
            ws.onopen = () => { opened = true; resolve(); };
            ws.onerror = () => { if (!opened) reject(new Error(t('Connection failed'))); };
            ws.onclose = (e) => {
                this.failAll(e.reason || t('Disconnected'));
                if (!opened) reject(new Error(e.reason || t('Connection failed')));
                else if (this.onClose) this.onClose(e);
            };
            ws.onmessage = (ev) => this.onMessage(ev);
        });
    }

    get isOpen() { return !!this.ws && this.ws.readyState === 1; }

    onMessage(ev) {
        if (typeof ev.data !== 'string') {
            const d = this.dl;
            if (d) {
                d.chunks.push(ev.data);
                d.got += ev.data.byteLength;
                if (d.onProgress) d.onProgress(d.got, d.size);
            }
            return;
        }
        let m;
        try { m = JSON.parse(ev.data); } catch (_) { return; }
        const d = this.dl;
        if (d && d.id === m.id) {
            if (m.error || m.ok === false) { this.dl = null; d.reject(new Error(m.error || t('Download failed'))); return; }
            if (m.done) { this.dl = null; d.resolve(new Blob(d.chunks)); return; }
            if (m.op === 'download') { d.size = Number(m.size) || 0; if (d.onProgress) d.onProgress(0, d.size); }
            return;
        }
        const p = this.pending.get(m.id);
        if (!p) {
            const l = this.listeners.get(m.id);
            if (l) l(m);
            return;
        }
        this.pending.delete(m.id);
        const fail = m.ok === false || !!m.error || (m.data && m.data.ok === false);
        if (fail) p.reject(new Error(m.error || m.data?.error || t('Operation failed')));
        else p.resolve(m.data || {});
    }

    /** 普通请求；binary 存在时紧跟一帧二进制（upload_chunk）。 */
    call(op, args = {}, binary) {
        if (!this.isOpen) return Promise.reject(new Error(t('Disconnected')));
        this.seq += 1;
        const id = this.seq;
        return new Promise((resolve, reject) => {
            this.pending.set(id, { resolve, reject });
            this.ws.send(JSON.stringify({ id, op, ...args }));
            if (binary) this.ws.send(binary);
        });
    }

    /** 下载 → Blob。同一时间只允许一个下载。 */
    download(path, onProgress) {
        if (!this.isOpen) return Promise.reject(new Error(t('Disconnected')));
        if (this.dl) return Promise.reject(new Error(t('A download is already in progress')));
        this.seq += 1;
        const id = this.seq;
        return new Promise((resolve, reject) => {
            this.dl = { id, chunks: [], got: 0, size: 0, onProgress, resolve, reject };
            this.ws.send(JSON.stringify({ id, op: 'download', path }));
        });
    }

    /** 推送型请求（logs_follow）：同 id 的所有消息交给 listener。 */
    follow(op, args, listener) {
        if (!this.isOpen) return null;
        this.seq += 1;
        const id = this.seq;
        this.listeners.set(id, listener);
        this.ws.send(JSON.stringify({ id, op, ...args }));
        return id;
    }

    unfollow(id) { this.listeners.delete(id); }

    failAll(reason) {
        const err = new Error(reason);
        this.pending.forEach((p) => p.reject(err));
        this.pending.clear();
        if (this.dl) { this.dl.reject(err); this.dl = null; }
    }

    close() {
        this.onClose = null;
        if (this.ws) { try { this.ws.close(); } catch (_) { /* ignore */ } }
        this.failAll(t('Disconnected'));
    }
}

/** 每次连接都取新票据（一次性）。卸载时关闭。返回 { sockRef, state, error, connect }。 */
export const useFilesSocket = (fetchTicket) => {
    const sockRef = useRef(null);
    const alive = useRef(true);
    const [state, setState] = useState('connecting');
    const [error, setError] = useState('');

    const connect = useCallback(async () => {
        if (sockRef.current) { sockRef.current.close(); sockRef.current = null; }
        setState('connecting');
        setError('');
        const r = await fetchTicket();
        if (!alive.current) return null;
        if (!isOk(r) || !r.data?.ws_url) {
            setState('closed');
            setError(errMsg(r, t('Connection failed')));
            return null;
        }
        const s = new FilesSocket();
        sockRef.current = s;
        s.onClose = (e) => {
            if (alive.current && sockRef.current === s) { setState('closed'); setError(e?.reason || ''); }
        };
        try {
            await s.open(buildWsUrl(r.data.ws_url, { token: r.data.token }));
            if (!alive.current || sockRef.current !== s) { s.close(); return null; }
            setState('open');
            return s;
        } catch (e) {
            if (alive.current && sockRef.current === s) { setState('closed'); setError(e.message); }
            return null;
        }
    }, [fetchTicket]);

    useEffect(() => {
        alive.current = true;
        connect();
        return () => {
            alive.current = false;
            if (sockRef.current) { sockRef.current.close(); sockRef.current = null; }
        };
    }, [connect]);

    return { sockRef, state, error, connect };
};
